import React from 'react'
import { FormattedMessage } from 'react-intl'
import { StatsRow, StatCard, StatLabel, StatValue, BreakdownRow, SectionHeading, ChartsRow, Wrapper, EmptyCard } from './style'
import Donutchartcard from '../Donutchartcard'
import ProgressSection from '../ProgressSection'
import AlertIconQuestion from '../AlertIconQuestion'
import { toPercentSegments } from '../../utils'

const PressureTab = ({ monitoring, pressureStatus, isDraft }) => {
  const pressureData = monitoring?.pressureBiodiversity

  const hasSpecies = (monitoring?.biodiversity?.speciesItems || 0) > 0
  const hasEcosystem = (monitoring?.biodiversity?.ecoItems || 0) > 0
  const visibleChartsCount = (hasSpecies ? 1 : 0) + (hasEcosystem ? 1 : 0)

  const speciesTotal = pressureData?.species?.notStarted + pressureData?.species?.started + pressureData?.species?.completed || 0
  const speciesPercent = speciesTotal > 0 ? Math.round(((pressureData?.species?.started + pressureData?.species?.completed) / speciesTotal) * 100) : 0

  const ecosystemsTotal = pressureData?.ecosystems?.notStarted + pressureData?.ecosystems?.started + pressureData?.ecosystems?.completed || 0
  const ecosystemsPercent =
    ecosystemsTotal > 0 ? Math.round(((pressureData?.ecosystems?.started + pressureData?.ecosystems?.completed) / ecosystemsTotal) * 100) : 0

  const existingCount =
    pressureData?.existingPressuresCount ??
    (pressureData?.pressureSummary?.filter((p) => p.previously_added_pressure)?.length || 0)
  const totalCount = pressureData?.pressuresTotal || 0
  const newAddedCount = pressureData?.newPressuresAddedCount ?? Math.max(0, totalCount - existingCount)

  const pressuresBreakdown = {
    existing: existingCount,
    newAdded: newAddedCount,
    total: totalCount
  }

  const triggersAdded = {
    current: pressureData?.pressuresWithTriggersAdded || 0,
    total: pressureData?.pressuresTotal || 0
  }

  const impactAssigned = {
    current: pressureData?.pressuresWithImpactAssigned || 0,
    total: pressureData?.pressuresTotal || 0
  }

  const triggersAddedPercent =
    totalCount > 0 ? Math.round((triggersAdded.current / triggersAdded.total) * 100) : 0
  const impactAssignedPercent =
    totalCount > 0 ? Math.round((impactAssigned.current / impactAssigned.total) * 100) : 0

  const subBars = [
    {
      labelId: 'monitoringTriggersAddedLabel',
      labelDefaultMessage: 'Triggers added',
      percent: triggersAddedPercent
    },
    {
      labelId: 'monitoringImpactsAssignedLabel',
      labelDefaultMessage: 'Impacts assigned',
      percent: impactAssignedPercent
    }
  ]

  return (
    <Wrapper data-test="monitoring-pressure-tab">
      <ProgressSection
        percent={pressureStatus?.progress}
        status={pressureStatus?.status}
        labelId="monitoringPressureEvaluationProgress"
        labelDefaultMessage="Pressure evaluation progress: {percent}%"
        isDraft={isDraft}
        subBars={subBars}
      />

      <StatsRow>
        <StatCard $breakDown>
          <BreakdownRow>
            <FormattedMessage id="monitoringExistingPressures" defaultMessage="Existing pressures" />
            <span>{pressuresBreakdown.existing}</span>
          </BreakdownRow>
          <BreakdownRow>
            <FormattedMessage id="monitoringNewPressuresAdded" defaultMessage="New pressures added" />
            <span>{pressuresBreakdown.newAdded}</span>
          </BreakdownRow>
          <BreakdownRow $emphasis>
            <FormattedMessage id="monitoringTotal" defaultMessage="Total" />
            <span>{pressuresBreakdown.total}</span>
          </BreakdownRow>
        </StatCard>

        <StatCard>
          <StatLabel>
            <FormattedMessage id="monitoringPressuresWithTriggersAdded" defaultMessage="Pressures with triggers added" />
          </StatLabel>
          <StatValue>
            {triggersAdded.current}
            <span>/{triggersAdded.total}</span>
          </StatValue>
        </StatCard>

        <StatCard>
          <StatLabel>
            <FormattedMessage
              id="monitoringPressuresWithImpactAssigned"
              defaultMessage="Pressures with impact assigned"
            />
          </StatLabel>
          <StatValue>
            {impactAssigned.current}
            <span>/{impactAssigned.total}</span>
          </StatValue>
        </StatCard>
      </StatsRow>

      <SectionHeading>
        <FormattedMessage
          id="monitoringPressureTriggerAssociations"
          defaultMessage="Pressure - trigger element associations"
        />
        <AlertIconQuestion title={<FormattedMessage id="monitoringTooltipPressureTriggerAssociations" defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua." />} />
      </SectionHeading>

      {visibleChartsCount > 0 ? (
        <ChartsRow $columns={visibleChartsCount}>
          {hasSpecies && (
            <Donutchartcard
              titleId="monitoringPercentSpecies"
              titleDefaultMessage="% species"
              segments={toPercentSegments(speciesPercent)}
              legendPosition="none"
              centerLabel={`${speciesPercent}%`}
              helpTextId="monitoringShareSpeciesTriggersPressure"
              helpTextDefaultMessage="Share of species triggers with an associated pressure"
            />
          )}
          {hasEcosystem && (
            <Donutchartcard
              titleId="monitoringPercentEcosystems"
              titleDefaultMessage="% ecosystems"
              segments={toPercentSegments(ecosystemsPercent)}
              legendPosition="none"
              centerLabel={`${ecosystemsPercent}%`}
              helpTextId="monitoringShareEcosystemTriggersPressure"
              helpTextDefaultMessage="Share of ecosystem triggers with an associated pressure"
            />
          )}
        </ChartsRow>
      ) : (
        <EmptyCard>
          <FormattedMessage
            id="monitoringNoSpeciesOrEcosystemsPressure"
            defaultMessage="No species or ecosystems available for pressure evaluation"
          />
        </EmptyCard>
      )}
    </Wrapper>
  )
}

export default PressureTab
